import { ModalTypes } from 'enums/modalTypes';
import {
  PersonalityTestActionFragment,
  ProjectPageGetProjectByIdDocument,
  SupportedLocale,
  useRemovePersonalityTestFromAssessmentStageMutation,
  useUpdatePersonalityTestQuestionsPerTraitCountMutation,
  useUpdatePersonalityTestSelectedLocalesMutation,
} from 'generated/graphql';
import { closeModal, openModal } from 'graphql/modals';
import { FC, useState } from 'react';
import { NotificationModalProps } from 'views/ModalView/NotificationModal';
import {
  getSupportedLocaleMetadata,
  SupportedLocale as L10nSupportedLocale,
} from '@sz/l10n';
import {
  Button,
  Modal,
  MultiselectFormField,
  NotificationModalType,
  TextInputField,
  TMultiselectOption,
  TNotification,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import {
  MAX_PERSONALITY_QUESTIONS_PER_TRAIT,
  MIN_PERSONALITY_QUESTIONS_PER_TRAIT,
} from '../helpers';

interface IEditPersonalityTestActionModal {
  action: PersonalityTestActionFragment;
  projectId: number;
  onClose: () => void;
}

const localeToOption = (locale: SupportedLocale): TMultiselectOption => ({
  label: getSupportedLocaleMetadata(L10nSupportedLocale[locale]).name.english,
  value: locale,
});

const localeOptions = Object.values(SupportedLocale).map(localeToOption);

const EditPersonalityTestActionModal: FC<IEditPersonalityTestActionModal> = ({
  action,
  projectId,
  onClose,
}) => {
  const { handleMsgType } = useNotification();
  const [submitting, setSubmitting] = useState(false);
  const [questionsPerTraitCount, setQuestionsPerTraitCount] = useState(
    action.questionsPerTraitCount.toString()
  );
  const [selectedLocales, setSelectedLocales] = useState<TMultiselectOption[]>(
    action.selectedLocales.map(localeToOption)
  );

  const parsedCount = Number(questionsPerTraitCount);
  const isCountValid =
    Number.isInteger(parsedCount) &&
    parsedCount >= MIN_PERSONALITY_QUESTIONS_PER_TRAIT &&
    parsedCount <= MAX_PERSONALITY_QUESTIONS_PER_TRAIT;
  const isLocalesValid = selectedLocales.length > 0;

  const refetchQueries = [
    { query: ProjectPageGetProjectByIdDocument, variables: { id: projectId } },
  ];

  const [updateQuestionsPerTraitCount] =
    useUpdatePersonalityTestQuestionsPerTraitCountMutation({ refetchQueries });

  const [updateSelectedLocales] =
    useUpdatePersonalityTestSelectedLocalesMutation({ refetchQueries });

  const [removePersonalityTest] =
    useRemovePersonalityTestFromAssessmentStageMutation({
      refetchQueries,
      onError: error => {
        handleMsgType({ type: TNotification.error, message: error?.message });
        setSubmitting(false);
      },
      onCompleted: () => {
        handleMsgType({
          type: TNotification.success,
          message: 'Personality test removed!',
        });
        onClose();
      },
    });

  const handleRemove = () => {
    const confirmationModalProps: NotificationModalProps = {
      message: 'This action will remove the personality test from the stage',
      okButtonChildren: 'OK',
      type: NotificationModalType.WARNING,
      onOkClick: () => {
        setSubmitting(true);
        closeModal(ModalTypes.NOTIFICATION_MODAL);
        removePersonalityTest({ variables: { id: action.id } });
      },
    };
    openModal(ModalTypes.NOTIFICATION_MODAL, confirmationModalProps);
  };

  const countChanged =
    questionsPerTraitCount !== action.questionsPerTraitCount.toString();
  const localesChanged =
    selectedLocales.length !== action.selectedLocales.length ||
    selectedLocales.some(
      option =>
        !action.selectedLocales.includes(option.value as SupportedLocale)
    );

  const handleSave = async () => {
    if (!countChanged && !localesChanged) {
      onClose();
      return;
    }

    setSubmitting(true);
    try {
      if (countChanged) {
        await updateQuestionsPerTraitCount({
          variables: { id: action.id, questionsPerTraitCount: parsedCount },
        });
      }
      if (localesChanged) {
        await updateSelectedLocales({
          variables: {
            id: action.id,
            selectedLocales: selectedLocales.map(
              option => option.value as SupportedLocale
            ),
          },
        });
      }
      handleMsgType({
        type: TNotification.success,
        message: 'Personality test updated!',
      });
      onClose();
    } catch (error) {
      handleMsgType({
        type: TNotification.error,
        message:
          error instanceof Error
            ? error.message
            : 'Could not update the personality test.',
      });
      setSubmitting(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      isOpen
      className="edit-test-action-modal"
      header="Personality test configuration"
    >
      <div className="edit-test-action-modal__content">
        <TextInputField
          id={`personality-test-action-${action.id}-questions-per-trait`}
          label="Number of questions/trait"
          placeholder="Number of questions/trait"
          value={questionsPerTraitCount}
          onChange={setQuestionsPerTraitCount}
        />
        {!isCountValid && (
          <div className="edit-test-action-modal__error">
            {`The number of questions per trait must be between ${MIN_PERSONALITY_QUESTIONS_PER_TRAIT} and ${MAX_PERSONALITY_QUESTIONS_PER_TRAIT}.`}
          </div>
        )}
        <MultiselectFormField
          id={`personality-test-action-${action.id}-locales`}
          label="Languages"
          placeholder="Languages"
          options={localeOptions}
          value={selectedLocales}
          onChange={(value: TMultiselectOption[]) => setSelectedLocales(value)}
        />
        {!isLocalesValid && (
          <div className="edit-test-action-modal__error">
            At least one language must be selected.
          </div>
        )}
      </div>
      <div className="edit-test-action-modal__actions">
        <Button
          onClick={handleRemove}
          size="medium"
          variant="negativeSecondary"
          disabled={submitting}
          className="edit-test-action-modal__remove-button"
          data-testid="edit-personality-test-action-modal__remove-button"
        >
          Remove
        </Button>
        <Button onClick={onClose} size="medium" variant="secondary">
          Cancel
        </Button>
        <Button
          size="medium"
          disabled={submitting || !isCountValid || !isLocalesValid}
          onClick={() => handleSave()}
          data-testid="edit-personality-test-action-modal__save-button"
        >
          Save
        </Button>
      </div>
    </Modal>
  );
};

export default EditPersonalityTestActionModal;
